import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useLanguage } from "../../../contexts/LanguageContext";

/** Bandeau signature : cuisines du monde, décoratif — pas de dépendance à l'API de traduction */
const CUISINES = {
  fr: ["Italie", "Japon", "Maroc", "Mexique", "Inde", "Liban", "Grèce", "Corée", "Vietnam"],
  en: ["Italy", "Japan", "Morocco", "Mexico", "India", "Lebanon", "Greece", "Korea", "Vietnam"],
  km: ["អ៊ីតាលី", "ជប៉ុន", "ម៉ារ៉ុក", "ម៉ិកស៊ិក", "ឥណ្ឌា", "លីបង់", "ក្រិក", "កូរ៉េ", "វៀតណាម"],
};

const textVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
  }),
};

/** Collage hero : 3 photos qui s'assemblent en cascade à l'entrée */
const photoVariants = {
  hidden: { opacity: 0, scale: 0.92, y: 16 },
  visible: (delay = 0) => ({
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
  }),
};

const HERO_PHOTOS = {
  main: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=900&h=1100&fit=crop&q=85",
  secondary: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=700&h=700&fit=crop&q=85",
  accent: "https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=500&h=500&fit=crop&q=85",
};

/**
 * Hero "Terracotta Studio" : collage photo en cascade (grande photo + 2 photos
 * satellites décalées) plutôt qu'une illustration géométrique abstraite — la
 * cuisine doit se voir dès le premier écran. Le vert pine et le triangle ember
 * restent en accents (cadre autour de la petite photo, sticker décoratif) pour
 * garder la continuité de marque plutôt que de tout miser sur la photo seule.
 */
const HeroSection = ({ onViewRecipes }) => {
  const { t, language } = useLanguage();
  const shouldReduceMotion = useReducedMotion();

  const scrollToServices = () => {
    document
      .getElementById("services")
      ?.scrollIntoView({ behavior: shouldReduceMotion ? "auto" : "smooth" });
  };

  const cuisines = CUISINES[language] || CUISINES.fr;
  const marqueeItems = [...cuisines, ...cuisines];

  return (
    <section className="relative w-full paper-field -mt-16 sm:-mt-[70px] pt-16 sm:pt-[70px] overflow-hidden">
      <div className="grain-overlay" aria-hidden />

      <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 px-5 sm:px-8 lg:px-8 pt-12 sm:pt-16 lg:pt-24 pb-14 sm:pb-20">
        {/* Colonne texte : sous l'image sur mobile (order-2), à gauche sur desktop (lg:order-1, inchangé) */}
        <div className="flex flex-col justify-center max-w-xl order-2 lg:order-1">
          <motion.p
            className="flex items-center gap-3 text-ember-700 text-xs sm:text-sm font-bold uppercase tracking-[0.2em] mb-6"
            initial="hidden"
            animate="visible"
            variants={textVariants}
          >
            <span className="h-[2px] w-6 bg-ember-600" aria-hidden />
            {t("worldMenus")}
          </motion.p>

          <h1 className="mb-6 sm:mb-7">
            <motion.span
              className="block font-poster uppercase text-6xl sm:text-7xl lg:text-8xl font-extrabold text-ink-900 leading-[0.92] tracking-tight"
              initial="hidden"
              animate="visible"
              variants={textVariants}
              custom={0.1}
            >
              {t("heroTitle")}
            </motion.span>
            <motion.span
              className="block font-display italic text-3xl sm:text-4xl lg:text-5xl font-semibold text-ember-600 mt-1 sm:mt-2"
              initial="hidden"
              animate="visible"
              variants={textVariants}
              custom={0.2}
            >
              {t("heroTitle2")}
            </motion.span>
          </h1>

          <motion.p
            className="text-ink-500 text-base sm:text-lg mb-9 leading-relaxed"
            initial="hidden"
            animate="visible"
            variants={textVariants}
            custom={0.32}
          >
            {t("heroSubtitle")}
          </motion.p>

          <motion.div
            className="flex flex-wrap items-center gap-6 sm:gap-8 mb-10 sm:mb-12"
            initial="hidden"
            animate="visible"
            variants={textVariants}
            custom={0.42}
          >
            <motion.button
              onClick={onViewRecipes}
              className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-lg bg-ember-600 text-ink-50 font-bold text-sm uppercase tracking-wide transition-colors duration-150 hover:bg-ember-500"
              whileHover={{ y: -2 }}
              whileTap={{ y: 0 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
            >
              {t("viewRecipesButton")}
              <span aria-hidden>→</span>
            </motion.button>

            <button
              type="button"
              onClick={scrollToServices}
              className="text-ink-900 hover:text-ember-700 text-sm font-bold uppercase tracking-wide pb-1 border-b-2 border-ink-900 hover:border-ember-700 transition-colors duration-150"
            >
              {t("ourServices")}
            </button>
          </motion.div>

          {/* Bande de confiance : un seul signal réel (note), pas de statistique inventée */}
          <motion.div
            className="flex items-center gap-5 pt-6 border-t border-ink-900/10"
            initial="hidden"
            animate="visible"
            variants={textVariants}
            custom={0.52}
          >
            <span
              className="flex items-center gap-1.5"
              aria-label={t("fiveStarsAria")}
            >
              <span className="text-ember-600 text-base leading-none" aria-hidden>
                ★★★★★
              </span>
              <span className="font-poster text-2xl text-ink-900 font-bold leading-none">4.9</span>
            </span>
            <span className="h-8 w-px bg-ink-900/15" aria-hidden />
            <p className="text-ink-500 text-sm leading-snug max-w-[10rem]">
              {t("recommended")}
            </p>
          </motion.div>
        </div>

        {/* Colonne collage : 3 photos en cascade décalée, accents pine/ember conservés. Au-dessus du texte sur mobile (order-1), à droite sur desktop (lg:order-2, inchangé) */}
        <div className="relative flex items-center justify-center w-full max-w-[360px] sm:max-w-[440px] lg:max-w-none mx-auto min-h-[340px] sm:min-h-[420px] lg:min-h-[460px] order-1 lg:order-2">
          {/* Photo principale */}
          <motion.div
            className="relative w-[220px] h-[280px] sm:w-[270px] sm:h-[340px] lg:w-[300px] lg:h-[380px] rounded-[28px] overflow-hidden shadow-xl shadow-ink-900/15 z-10"
            initial={shouldReduceMotion ? undefined : "hidden"}
            animate={shouldReduceMotion ? undefined : "visible"}
            custom={0.1}
            variants={photoVariants}
            whileHover={shouldReduceMotion ? undefined : { scale: 1.02 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <img
              src={HERO_PHOTOS.main}
              alt=""
              className="w-full h-full object-cover"
              loading="eager"
              decoding="async"
            />
          </motion.div>

          {/* Photo satellite, cadrée de vert pine pour garder l'accent de marque */}
          <motion.div
            className="absolute right-0 bottom-4 sm:bottom-6 w-[140px] h-[140px] sm:w-[170px] sm:h-[170px] rounded-3xl bg-pine-700 p-2 shadow-xl shadow-ink-900/15 z-20"
            initial={shouldReduceMotion ? undefined : "hidden"}
            animate={shouldReduceMotion ? undefined : "visible"}
            custom={0.28}
            variants={photoVariants}
            whileHover={shouldReduceMotion ? undefined : { scale: 1.04, rotate: -2 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="w-full h-full rounded-2xl overflow-hidden">
              <img
                src={HERO_PHOTOS.secondary}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </motion.div>

          {/* Petit accent photo + sticker ember, en écho au triangle de l'ancienne illustration */}
          <motion.div
            className="absolute left-0 top-6 sm:top-8 w-[92px] h-[92px] sm:w-[104px] sm:h-[104px] rounded-2xl overflow-hidden border-4 border-ink-50 shadow-lg shadow-ink-900/15 z-20 rotate-[-6deg]"
            initial={shouldReduceMotion ? undefined : "hidden"}
            animate={shouldReduceMotion ? undefined : "visible"}
            custom={0.42}
            variants={photoVariants}
            whileHover={shouldReduceMotion ? undefined : { scale: 1.06, rotate: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <img
              src={HERO_PHOTOS.accent}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
          </motion.div>
          <div
            className="absolute left-[70px] top-[18px] sm:left-[82px] sm:top-[22px] w-5 h-5 sm:w-6 sm:h-6 bg-ember-500 rotate-[12deg] z-30"
            style={{ clipPath: "polygon(50% 0%, 100% 100%, 0% 100%)" }}
            aria-hidden
          />

          <div className="absolute top-0 left-2 sm:left-4 bg-ink-900 text-ink-50 rounded-lg px-3.5 py-2 text-xs font-bold uppercase tracking-wide shadow-lg shadow-ink-900/10 z-30">
            ★ 4.9
          </div>
          <div className="absolute bottom-0 right-2 sm:right-4 bg-ink-50 text-ink-900 border border-ink-900 rounded-lg px-3.5 py-2 text-xs font-bold uppercase tracking-wide z-30">
            {t("worldMenus")}
          </div>
        </div>
      </div>

      {/* Marquee : signature de marque, communique la diversité des cuisines couvertes */}
      <div className="relative border-t border-ink-900/10 bg-ink-900/[0.02] py-3.5 sm:py-4 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap" aria-hidden>
          {marqueeItems.map((c, i) => (
            <span
              key={i}
              className="inline-flex items-center text-ink-500 text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] px-5 sm:px-7"
            >
              {c}
              <span className="ml-5 sm:ml-7 text-ember-600/70" aria-hidden>
                ·
              </span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
